import React, { useState, useMemo, useCallback } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    TextField,
    InputAdornment,
    Typography,
    Box,
    IconButton,
    Button,
    CircularProgress,
} from '@mui/material'
import { Trans } from '@lingui/macro'
import { Staff } from '@/core/models/staff.model'
import { ChatRoom } from '@/core/models/chat-room.model'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'

type ContactPickerModalProps = {
    open: boolean
    onClose: () => void
    staffs: Staff[]
    onContactSelect: (staffId: string) => void
    currentUserId: string
    existingPrivateChats: ChatRoom[]
    onLoadMore?: () => void
    isLoadingMore?: boolean
    hasMore?: boolean
}

export function ContactPickerModal({
    open,
    onClose,
    staffs,
    onContactSelect,
    currentUserId,
    existingPrivateChats,
    onLoadMore,
    isLoadingMore = false,
    hasMore = false,
}: ContactPickerModalProps) {
    const [searchTerm, setSearchTerm] = useState('')

    // Filter out current user and apply search
    const filteredStaffs = useMemo(() => {
        return staffs
            .filter((staff) => staff._id !== currentUserId)
            .filter((staff) => {
                const fullName = `${staff.userInfo.name} ${staff.userInfo.surname}`.toLowerCase()
                return fullName.includes(searchTerm.toLowerCase())
            })
    }, [staffs, currentUserId, searchTerm])

    const handleLoadMore = useCallback(() => {
        if (onLoadMore && hasMore && !isLoadingMore) {
            onLoadMore()
        }
    }, [onLoadMore, hasMore, isLoadingMore])

    // Check if a private chat already exists with a staff member
    const hasExistingChat = (staffId: string) => {
        return existingPrivateChats.some((chat) =>
            chat.isPrivate && chat.users.some((user) => user._id === staffId)
        )
    }

    const handleContactClick = (staffId: string) => {
        onContactSelect(staffId)
    }

    const getInitials = (name: string, surname: string) => {
        return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { height: '70vh' },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    '& .MuiTypography-root': {
                        fontSize: '1.25rem',
                        fontWeight: 500,
                    }
                }}
            >
                <Trans>Start New Chat</Trans>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Search Bar */}
                <Box sx={{ p: 2, pb: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Search contacts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        size="small"
                    />
                </Box>

                {/* Contact List */}
                <List sx={{ pt: 0 }}>
                    {filteredStaffs.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem', fontWeight: 400 }}>
                            {searchTerm ? (
                                <Trans>No contacts found</Trans>
                            ) : (
                                <Trans>No contacts available</Trans>
                            )}
                        </Box>
                    ) : (
                        filteredStaffs.map((staff) => {
                            const fullName = `${staff.userInfo.name} ${staff.userInfo.surname}`
                            const hasChat = hasExistingChat(staff._id)

                            return (
                                <ListItem
                                    key={staff._id}
                                    onClick={() => handleContactClick(staff._id)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            {getInitials(staff.userInfo.name, staff.userInfo.surname)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">{fullName}</Typography>
                                                {hasChat && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        (existing chat)
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                {staff.email || ''}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            )
                        })
                    )}

                    {/* Load More Button */}
                    {hasMore && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                                variant="outlined"
                                size="small"
                                startIcon={isLoadingMore ? <CircularProgress size={16} /> : null}
                            >
                                {isLoadingMore ? <Trans>Loading...</Trans> : <Trans>Load More</Trans>}
                            </Button>
                        </Box>
                    )}
                </List>
            </DialogContent>
        </Dialog>
    )
}
